import { AlertTriangle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { isEqual } from 'lodash-es';
import { useSuspensePolicy } from '@http/queries/useFetchPolicy';
import {
  useUpdatePolicy,
  type UpdatePolicyBody,
} from '@http/queries/useUpdatePolicy';
import LoadingSpinner from '@components/LoadingSpinner';
import ConfirmDialog from '@components/ConfirmDialog';
import PolicyForm, { policyToInitialValues } from './PolicyForm';
import { useRequiredRouteId } from '@/hooks/useRequiredRouteId';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { usePolicyRescan } from './usePolicyRescan';
import { useEditableGovernanceDomainOptions } from './governanceDomainOptions';
import { usePolicyEditAccess } from './usePolicyEditAccess';

function EditPolicy() {
  const { t } = useInlineTranslation();
  const navigate = useNavigate();
  const id = useRequiredRouteId('policyId');

  const { data: policy } = useSuspensePolicy(id);
  const domainOptions = useEditableGovernanceDomainOptions(
    policy.auditEntryIds,
  );
  const { canEditPolicy, isPending: isEditAccessPending } =
    usePolicyEditAccess();
  const updatePolicy = useUpdatePolicy();
  const { rescanPolicy } = usePolicyRescan(policy.auditEntryIds);
  const [pendingRuleUpdate, setPendingRuleUpdate] =
    useState<UpdatePolicyBody | null>(null);

  const initialValues = useMemo(() => policyToInitialValues(policy), [policy]);

  // The boundary above only proves the user may see policies; editing this one
  // needs editor access to every domain it covers, as `policies PUT` checks.
  if (isEditAccessPending) {
    return <LoadingSpinner />;
  }

  if (!canEditPolicy(policy)) {
    return <Navigate to='/policies' replace />;
  }

  const savePolicy = (body: UpdatePolicyBody) => {
    if (!isEqual(policy.rule, body.rule)) {
      setPendingRuleUpdate(body);
      return;
    }

    void updatePolicy.mutate(body, {
      onSuccess: (updatedPolicy) => {
        void rescanPolicy(updatedPolicy, { showSuccessNotification: false });
        void navigate('/policies');
      },
    });
  };

  const confirmRuleUpdate = () => {
    if (!pendingRuleUpdate) return;

    void updatePolicy.mutate(pendingRuleUpdate, {
      onSuccess: (updatedPolicy) => {
        void rescanPolicy(updatedPolicy, { showSuccessNotification: false });
        void navigate('/policies');
      },
    });
  };

  return (
    <>
      <PolicyForm
        heading={t('editPolicy')}
        description={t('editPolicyDescription')}
        submitLabel={t('saveChanges')}
        canSaveAsDraft={false}
        isSubmitting={updatePolicy.isPending}
        initialValues={initialValues}
        domainOptions={domainOptions}
        onSubmit={(body) => savePolicy({ ...body, id })}
      />
      <ConfirmDialog
        open={pendingRuleUpdate !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRuleUpdate(null);
        }}
        title={t('updatePolicyRule')}
        description={t('updatePolicyRuleConfirmation')}
        confirmText={t('updateRule')}
        confirmIcon={<AlertTriangle aria-hidden />}
        loading={updatePolicy.isPending}
        onConfirm={confirmRuleUpdate}
      />
    </>
  );
}

export { EditPolicy as Component };
export default EditPolicy;
